import { computed } from 'vue'

import { PermissionActions } from '@/features/permissions/permissionActions.js'
import { useAppEnvironmentMode } from '@/features/environment/useAppEnvironmentMode.js'
import { usePermissionItemFilter } from '@/features/permissions/filterPermissionItems.js'
import { superAdminLogsSidebarChildren } from '@/features/superAdmin/logsManagement/superAdminLogsNavigation.js'
import { useSuperAdminIdentity } from '@/features/superAdmin/useIsSuperAdmin.js'
import {
  SuperAdminLimitedLogsViewActions,
  SuperAdminLimitedMasterViewActions,
  SuperAdminLimitedOASystemNavigationActions,
} from '@/features/superAdmin/superAdminLimitedPermissionActions.js'

export const superAdminMasterSidebarChildren = [
  {
    label: 'sidebar.masterSub.brands',
    iconClass: 'pi pi-tag',
    to: '/super-admin/master/brands',
    permissionAction: PermissionActions.SuperAdminLimitedMasterBrandsView,
  },
  {
    label: 'sidebar.masterSub.components',
    iconClass: 'pi pi-box',
    to: '/super-admin/master/components',
    permissionAction: PermissionActions.SuperAdminLimitedMasterComponentsView,
  },
  {
    label: 'sidebar.masterSub.countries',
    iconClass: 'pi pi-globe',
    to: '/super-admin/master/countries',
    permissionAction: PermissionActions.SuperAdminLimitedMasterCountriesView,
  },
  {
    label: 'sidebar.masterSub.sportCategories',
    iconClass: 'pi pi-sitemap',
    to: '/super-admin/master/sport-categories',
    permissionAction: PermissionActions.SuperAdminLimitedMasterSportCategoriesView,
  },
  {
    label: 'sidebar.masterSub.tenantDefinitions',
    iconClass: 'pi pi-building',
    to: '/super-admin/master/tenant-definitions',
    permissionAction: PermissionActions.SuperAdminLimitedMasterTenantDefinitionsView,
  },
  {
    label: 'sidebar.masterSub.terminologies',
    iconClass: 'pi pi-language',
    to: '/super-admin/master/terminologies',
    permissionAction: PermissionActions.SuperAdminLimitedMasterTerminologiesView,
  },
  {
    label: 'sidebar.masterSub.profileOrganizationRoles',
    iconClass: 'pi pi-id-card',
    to: '/super-admin/master/profile-organization-roles',
    permissionAction: PermissionActions.SuperAdminLimitedMasterProfileOrganizationRolesView,
  },
  {
    label: 'sidebar.masterSub.sportTypes',
    iconClass: 'pi pi-flag',
    to: '/super-admin/master/sport-types',
    permissionAction: PermissionActions.SuperAdminLimitedMasterSportTypesView,
  },
  {
    label: 'sidebar.masterSub.osiAccessModules',
    iconClass: 'pi pi-key',
    to: '/super-admin/master/osi-access',
    permissionAction: PermissionActions.SuperAdminLimitedMasterOsiAccessModulesView,
  },
]

export const superAdminSidebarMenuLinkItems = [
  {
    label: 'sidebar.clubs',
    icon: 'people',
    to: '/super-admin/clubs',
    permissionAction: PermissionActions.SuperAdminLimitedClubsView,
  },
  {
    label: 'sidebar.dashboard',
    icon: 'dashboard',
    to: '/super-admin/dashboard',
    permissionAction: PermissionActions.SuperAdminLimitedDashboardView,
  },
  {
    label: 'sidebar.people',
    iconClass: 'pi pi-users',
    to: '/super-admin/people',
    permissionAction: PermissionActions.SuperAdminLimitedPeopleView,
  },
  {
    label: 'Super Admin Users',
    iconClass: 'pi pi-user-edit',
    to: '/super-admin/users',
    fullSuperAdminOnly: true,
  },
  {
    label: 'Roles & Permissions',
    iconClass: 'pi pi-lock',
    to: '/super-admin/role-management',
    permissionAction: PermissionActions.SuperAdminLimitedRolesView,
  },
  {
    label: 'sidebar.master',
    icon: 'settings',
    to: '/super-admin/master',
    permissionAnyActions: SuperAdminLimitedMasterViewActions,
    children: superAdminMasterSidebarChildren,
  },
  {
    label: 'Global Site Themes',
    iconClass: 'pi pi-globe',
    to: '/super-admin/global-site-themes',
    permissionAction: PermissionActions.SuperAdminLimitedGlobalSiteThemesView,
  },
  {
    label: 'sidebar.logs',
    iconClass: 'pi pi-file',
    to: '/super-admin/logs',
    permissionAnyActions: SuperAdminLimitedLogsViewActions,
    children: superAdminLogsSidebarChildren,
  },
  {
    label: 'OA System',
    iconClass: 'pi pi-database',
    to: '/super-admin/oa-system',
    permissionAnyActions: SuperAdminLimitedOASystemNavigationActions,
    uat2Only: true,
  },
]

export function useSuperAdminSidebarMenuLinks() {
  const { isFullSuperAdmin } = useSuperAdminIdentity()
  const { isUat2Mode } = useAppEnvironmentMode()
  const { filterItems } = usePermissionItemFilter('super-admin')

  return computed(() =>
    filterItems(
      superAdminSidebarMenuLinkItems.filter(
        item =>
          (!item.fullSuperAdminOnly || isFullSuperAdmin.value) && (!item.uat2Only || isUat2Mode),
      ),
    ),
  )
}
